// Path: ./src/app/pages/CreateUserPage/index.tsx
import { Checkbox, FormControlLabel, Grid, Box } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { adminApiService } from "../../../utils/api";
import { useParams } from "react-router-dom";

import {
  GfMerchant,
  GfRole,
  GfStore,
  GfStoreDto,
  UpdateGfAdminUserDto,
} from "@swagger/typescript-fetch-goflyer";
import { useLocation, useNavigate } from "react-router-dom";

import { messages } from "../../../locales/messages";
import { useTranslation } from "react-i18next";
import { combineAndRemoveDuplicates } from "../../../utils/utils";
import { toastWarn } from "../../../utils/pageUtil/toast";
import { makeStoreDisplayName } from "../../../utils/dtoUtil/makeStoreDisplayName";
import { debounce } from "../../../utils/debounce";
import { useFlyerItemCategories } from "../../../hooks";
import { useRoles } from "../../../hooks/useRoles";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { useMerchants } from "../../../hooks/useMerchants";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { CustomAutocompleteMultiSelect } from "../../components/CustomAutocomplete";
import {
  SetupPageContainer,
  TextInputField,
} from "../../../utils/commonStyledComponents/commonStyled";
import { useHandleSaveAdminUser } from "./handleSaveAdminUser";
import {
  CancelButton,
  ColorButton,
} from "../../../utils/commonStyledComponents/commonMuiStyled";
import { SUPERADMIN } from "../../../utils/constant/stringConstant";
import {
  GridWrapper,
  Wrapper,
  ButtonWrapper,
  InputLabelText,
} from "../../components/AccountPageCreatePageCommon";

import { MySettingsSection } from "../../components/AdminUserSettingsPage/MySettingsSection";

const CreateUserPage = () => {
  const handleSaveAdminUser = useHandleSaveAdminUser();
  // 1. React Router hooks
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // 2. Custom hooks - Localization and Data fetching
  const { t } = useTranslation();
  const { flyerItemCategories } = useFlyerItemCategories();
  const { rolesList } = useRoles();
  const { merchantsList, isMerchantOptionLoading, handleOnMerchantSearch } =
    useMerchants();

  // 3. State for Dtos, models and data
  const authHeaders = useAuthHeaders();
  const [updateUser, setUpdateUser] = useState<UpdateGfAdminUserDto>();
  const [storesList, setStoresList] = useState<GfStore[]>();
  const [isOptionLoadingStore, setIsOptionLoadingStore] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState<string>();

  const getStoreList = async (storeListValue?: typeof storesList) => {
    setIsOptionLoadingStore(true);
    let conditions = undefined;

    const merchantList = updateUser?.merchant?.map((value) => {
      return `${value.id}`;
    });

    const conditionA = [];

    if (merchantList && merchantList.length) {
      conditionA.push(`merchant.id||$in||${merchantList?.join(",")}`);
    }
    conditions = conditionA;
    const responseStore =
      await adminApiService.getManyBaseGfStoreAdminControllerGfStore(
        undefined,
        undefined,
        conditions,
        undefined,
        undefined,
        ["merchant"],
        100,
        0,
        1,
        undefined,
      );

    const stores = responseStore?.data?.map((value: GfStoreDto) => {
      return {
        ...value,
        label: makeStoreDisplayName(value),
        value: value.id,
      };
    });
    const resultOfStoreList = combineAndRemoveDuplicates(
      storeListValue,
      stores,
      "value" as keyof GfStore,
    );

    setStoresList(resultOfStoreList);
    setIsOptionLoadingStore(false);
  };

  const handleUserName = (value?: React.ChangeEvent<HTMLInputElement>) => {
    if (value) {
      const text = value.target.value;
      if (text.indexOf(" ") >= 0) {
        toastWarn(t(messages.Space_is_not_allowed_please_type_without_space()));
        return;
      }
      setUpdateUser({
        ...updateUser,
        userName: text,
      });
    }
  };
  const handleEmail = (value?: React.ChangeEvent<HTMLInputElement>) => {
    if (value) {
      const text = value.target.value;
      if (text.indexOf(" ") >= 0) {
        //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) this is wrong there should be global method which should take message and hide the other details of toast method and its library
        toastWarn(t(messages.What_do_you_want_to_do()));
        return;
      }
      setUpdateUser({
        ...updateUser,
        email: value.target.value,
      });
    }
  };
  const handleFirstName = (value?: React.ChangeEvent<HTMLInputElement>) => {
    value &&
      setUpdateUser({
        ...updateUser,
        firstName: value.target.value,
      });
  };
  const handleLastName = (value?: React.ChangeEvent<HTMLInputElement>) => {
    value &&
      setUpdateUser({
        ...updateUser,
        lastName: value.target.value,
      });
  };
  const handlePassword = (value?: React.ChangeEvent<HTMLInputElement>) => {
    if (value) {
      const text = value.target.value;
      if (text.indexOf(" ") >= 0) {
        toastWarn(t(messages.What_do_you_want_to_do()));
        return;
      }
      setUpdateUser({
        ...updateUser,
        password: text,
      });
    }
  };
  const handleConfirmPassword = (
    value?: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (value) {
      const text = value.target.value;
      setConfirmPassword(text);
      if (text.indexOf(" ") >= 0) {
        toastWarn(t(messages.What_do_you_want_to_do()));
        return;
      }
      //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) use a debounce to check the confirm password
    }
  };

  const handleSettingsPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUpdateUser({
      ...updateUser,
      settingsPage: event.target.checked,
    });
  };

  const handleRoleSelect = (value?: GfRole[]) => {
    value &&
      setUpdateUser({
        ...updateUser,
        roles: value,
      });

    /**
     * if role is super admin, we don't attach this user to any merchant or stores
     * super admin can managing all stores and all merchant
     */
    if (value?.length && value[0]?.name === SUPERADMIN) {
      setUpdateUser((prev) => ({
        ...prev,
        merchant: [],
        stores: [],
        gfFlyerItemCategory: [],
        settingsPage: true,
      }));
    } else {
      setUpdateUser((prev) => ({
        ...prev,
        settingsPage: false,
      }));
    }
  };

  const handleMerchantSelect = (value?: GfMerchant[]) => {
    value &&
      setUpdateUser({
        ...updateUser,
        merchant: value,
      });
  };

  const handleStoresSelect = (value?: GfStore[]) => {
    value &&
      setUpdateUser({
        ...updateUser,
        stores: value,
      });
  };
  const handleCancel = async () => {
    navigate(-1);
  };

  useEffect(() => {
    if (!location?.state?.createUser && !location?.state?.data) {
      getStoreList();
    }
  }, [updateUser?.merchant]);

  const handleOnStoreSearch = useCallback(
    debounce(async (value: string) => {
      if (value === "") {
        return;
      }
      setIsOptionLoadingStore(true);
      const selectedMerchant = updateUser?.merchant?.length
        ? [...updateUser.merchant.map((val) => val.id)]
        : [];
      const response =
        await adminApiService.gfStoreAdminControllerSearchByMerchant(
          1 as number,
          100 as number,
          value,
          [...selectedMerchant],
        );
      setStoresList(response.data);
      setIsOptionLoadingStore(false);
      //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) you can't search if for other role?
    }, 500),
    [],
  );

  useEffect(() => {
    if (userId) {
      (async function () {
        const user =
          await adminApiService.getOneBaseGfAdminUserAdminControllerGfAdminUser(
            userId,
            undefined,
            undefined,
            undefined,
            authHeaders,
          );
        setUpdateUser(user);
      })();
    }
  }, []);

  return (
    <SetupPageContainer data-testid="create-user-page">
      <AccountCardWrapper
        title={
          updateUser?.id ? t(messages.Editing_User()) : t(messages.Add_User())
        }
      >
        <GridWrapper>
          <Grid
            container
            spacing={2}
            style={{ marginTop: 10, maxWidth: 700, padding: 10, width: "100%" }}
          >
            <Grid
              item
              xs={12}
              md={12}
              style={{
                width: "100%",
              }}
            >
              <Wrapper>
                <InputLabelText>
                  {t(messages.UserName())} ({t(messages.required())})
                </InputLabelText>
                <TextInputField
                  value={updateUser?.userName}
                  type="text"
                  data-testid="create-user-username-input"
                  onChange={handleUserName}
                />
              </Wrapper>
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              style={{
                width: "100%",
              }}
            >
              <Wrapper>
                <InputLabelText>
                  {t(messages.Email())}
                  {!updateUser?.id && ` (${t(messages.required())})`}
                </InputLabelText>
                <TextInputField
                  placeholder={
                    updateUser?.id
                      ? t(messages.Leave_Empty_To_Keep_Current())
                      : undefined
                  }
                  value={updateUser?.email}
                  type="text"
                  data-testid="create-user-email-input"
                  onChange={handleEmail}
                />
              </Wrapper>
            </Grid>{" "}
            <Grid
              item
              xs={12}
              md={12}
              style={{
                width: "100%",
              }}
            >
              <Wrapper>
                <InputLabelText>{t(messages.First_Name())}</InputLabelText>
                <TextInputField
                  value={updateUser?.firstName}
                  data-testid="create-user-first-name-input"
                  type="text"
                  onChange={handleFirstName}
                />
              </Wrapper>
            </Grid>{" "}
            <Grid
              item
              xs={12}
              md={12}
              style={{
                width: "100%",
              }}
            >
              <Wrapper>
                <InputLabelText>{t(messages.Last_Name())}</InputLabelText>
                <TextInputField
                  value={updateUser?.lastName || ""}
                  data-testid="create-user-last-name-input"
                  type="text"
                  onChange={handleLastName}
                />
              </Wrapper>
            </Grid>{" "}
            <Grid
              item
              xs={12}
              md={12}
              style={{
                width: "100%",
              }}
            >
              <Wrapper>
                <InputLabelText>
                  {t(messages.Password())}
                  {!updateUser?.id && ` (${t(messages.required())})`}
                </InputLabelText>
                <TextInputField
                  placeholder={
                    updateUser?.id
                      ? t(messages.Leave_Empty_To_Keep_Current())
                      : undefined
                  }
                  value={updateUser?.password}
                  data-testid="create-user-password-input"
                  type="password"
                  onChange={handlePassword}
                />
              </Wrapper>
            </Grid>{" "}
            <Grid
              item
              xs={12}
              md={12}
              style={{
                width: "100%",
              }}
            >
              <Wrapper>
                <InputLabelText>
                  {t(messages.Confirm())} {t(messages.Password())}
                </InputLabelText>
                <TextInputField
                  value={confirmPassword}
                  data-testid="create-user-confirm-password-input"
                  type="password"
                  onChange={handleConfirmPassword}
                />
              </Wrapper>
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              style={{
                width: "100%",
              }}
            >
              <Wrapper>
                <InputLabelText>{t(messages.Role())}</InputLabelText>
                <CustomAutocompleteMultiSelect
                  optionsList={rolesList?.map((role) => ({
                    value: role,
                    label: role.name,
                  }))}
                  isLoading={isMerchantOptionLoading}
                  selectedValues={updateUser?.roles?.map((role) => ({
                    value: role,
                    label: role.name,
                  }))}
                  multiSelect={false}
                  handleOnChange={handleRoleSelect}
                  handleOnSearch={handleOnMerchantSearch}
                  dataTestId="create-user-role-select"
                />
              </Wrapper>
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              style={{
                width: "100%",
              }}
            >
              {updateUser?.roles?.length ? (
                <Wrapper>
                  <InputLabelText>{t(messages.SETTINGS_PAGE())}</InputLabelText>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={updateUser?.settingsPage}
                        onChange={handleSettingsPage}
                        sx={{
                          color: "#f58220",
                          "&.Mui-checked": {
                            color: "#f58220",
                          },
                        }}
                      />
                    }
                    label={t(
                      messages.allow_user_creation_without_merchant_store_category(),
                    )}
                    labelPlacement="start"
                  />
                </Wrapper>
              ) : (
                <></>
              )}
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              style={{
                width: "100%",
              }}
            >
              {updateUser?.roles?.[0]?.name !== SUPERADMIN &&
                (typeof updateUser?.settingsPage !== "boolean" ||
                  !updateUser.settingsPage) && (
                  <Wrapper>
                    <InputLabelText>{t(messages.Merchant())}</InputLabelText>
                    <CustomAutocompleteMultiSelect
                      optionsList={merchantsList?.map((merchant) => ({
                        value: merchant,
                        label: merchant.name,
                      }))}
                      isLoading={isMerchantOptionLoading}
                      selectedValues={updateUser?.merchant?.map((merchant) => ({
                        value: merchant,
                        label: merchant.name,
                      }))}
                      multiSelect={true}
                      handleOnChange={handleMerchantSelect}
                      handleOnSearch={handleOnMerchantSearch}
                      dataTestId="create-user-merchant-select"
                    />
                  </Wrapper>
                )}
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              style={{
                width: "100%",
              }}
            >
              {updateUser?.roles?.[0]?.name !== SUPERADMIN &&
                (typeof updateUser?.settingsPage !== "boolean" ||
                  !updateUser.settingsPage) && (
                  <Wrapper>
                    <InputLabelText>{t(messages.Stores())}</InputLabelText>
                    <div>
                      <CustomAutocompleteMultiSelect
                        optionsList={storesList?.map((store) => ({
                          value: store,
                          label: makeStoreDisplayName(store),
                        }))}
                        isLoading={isOptionLoadingStore}
                        selectedValues={updateUser?.stores?.map((store) => ({
                          value: store,
                          label: makeStoreDisplayName(store),
                        }))}
                        multiSelect={true}
                        handleOnChange={handleStoresSelect}
                        handleOnSearch={handleOnStoreSearch}
                        dataTestId="create-user-store-select"
                      />
                    </div>
                  </Wrapper>
                )}
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              style={{
                width: "100%",
              }}
            >
              {updateUser?.roles?.[0]?.name !== SUPERADMIN &&
                (typeof updateUser?.settingsPage !== "boolean" ||
                  !updateUser.settingsPage) && (
                  <Wrapper>
                    <InputLabelText>{t(messages.Category())}</InputLabelText>
                    <CustomAutocompleteMultiSelect
                      allowSelectAll={true}
                      optionsList={flyerItemCategories.map((fic) => ({
                        value: fic,
                        label: fic.name,
                      }))}
                      selectedValues={updateUser?.gfFlyerItemCategory?.map(
                        (category) => ({
                          value: category,
                          label: category.name,
                        }),
                      )}
                      multiSelect={true}
                      isLoading={false}
                      handleOnChange={(value) => {
                        setUpdateUser({
                          ...updateUser,
                          gfFlyerItemCategory: value,
                        });
                      }}
                      WrapperCss={`
                  flex-direction: row;
                  align-items: center;
                  justify-content: space-between;
                  width: 18.75rem;
                  @media (max-width: 457px) {
                    flex-direction: column;
                    align-items: start;
                    justify-content: start;
                  }
                  `}
                      dataTestId="create-user-category-select"
                    />
                  </Wrapper>
                )}
            </Grid>
            <Grid
              item
              xs={12}
              md={12}
              style={{
                width: "100%",
              }}
            >
              <Wrapper>
                <InputLabelText>
                  {t(messages.Admin_User_Settings())}
                </InputLabelText>
                <Box sx={{ mt: 2, pointerEvents: "none" }}>
                  <MySettingsSection
                    overrides={(updateUser?.settings ?? []).map((item) => ({
                      ...item,
                      setting: item.setting ?? item,
                    }))}
                    onSave={() => undefined}
                    onReset={() => undefined}
                    readonly={true}
                  />
                  {(updateUser?.settings ?? []).length === 0 && (
                    <span
                      style={{
                        color: "gray",
                        fontSize: "0.875rem",
                        fontStyle: "italic",
                        marginLeft: "4px",
                      }}
                    >
                      None
                    </span>
                  )}
                </Box>
              </Wrapper>
            </Grid>
            <Grid item xs={12}>
              <ButtonWrapper>
                <CancelButton
                  variant="contained"
                  onClick={handleCancel}
                  data-testid="create-user-cancel-button"
                >
                  {t(messages.Cancel())}
                </CancelButton>
                <ColorButton
                  variant="contained"
                  onClick={() => handleSaveAdminUser(updateUser)}
                  data-testid="create-user-finish-button"
                >
                  {updateUser?.id ? "Update" : t(messages.Finish())}
                </ColorButton>
              </ButtonWrapper>
            </Grid>
          </Grid>
        </GridWrapper>
      </AccountCardWrapper>
    </SetupPageContainer>
  );
};
export default CreateUserPage;
